// Resolution pipeline — expert analysis + 4-tier search + instruction generation
import { useState, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { executeAgent } from '@/modules/agents/agent-client'
import { getAgentForFeature } from '@/hooks/useOrgSettings'
import { getExpertFeatureForIssue, getExpertiseDomain, getExpertLabel } from '@/lib/expert-module-map'
import type { ResolutionTierResult, ResolutionTierName, GeneratedInstructions } from '@/types/issue'

interface PipelineParams {
  issueId: string
  description: string
  d365Version?: string
  d365Module?: string
  expertiseDomain?: string
}

interface ExpertAnalysisResult {
  expertFeature: string
  expertLabel: string
  analysis: string
  isRunning: boolean
}

interface PipelineState {
  isRunning: boolean
  currentTier: ResolutionTierName | null
  pausedAtTier: ResolutionTierName | null
  tiers: ResolutionTierResult[]
  bestOverallConfidence: number
  acceptedTier: ResolutionTierName | null
  expertAnalysis: ExpertAnalysisResult | null
}

const INITIAL_TIERS: ResolutionTierResult[] = [
  { tier: 'kb', status: 'pending', results: null, bestConfidence: 0, timestamp: null },
  { tier: 'ms_docs', status: 'pending', results: null, bestConfidence: 0, timestamp: null },
  { tier: 'community', status: 'pending', results: null, bestConfidence: 0, timestamp: null },
  { tier: 'expert', status: 'pending', results: null, bestConfidence: 0, timestamp: null },
]

export function useResolutionPipeline() {
  const [state, setState] = useState<PipelineState>({
    isRunning: false, currentTier: null, pausedAtTier: null,
    tiers: [...INITIAL_TIERS], bestOverallConfidence: 0, acceptedTier: null, expertAnalysis: null,
  })
  const paramsRef = useRef<PipelineParams | null>(null)

  const updateTier = useCallback((tier: ResolutionTierName, update: Partial<ResolutionTierResult>) => {
    setState((prev) => ({
      ...prev,
      tiers: prev.tiers.map((t) => t.tier === tier ? { ...t, ...update } : t),
    }))
  }, [])

  const runTier = useCallback(async (tierName: ResolutionTierName, params: PipelineParams) => {
    setState((prev) => ({ ...prev, isRunning: true, currentTier: tierName, pausedAtTier: null }))
    updateTier(tierName, { status: 'running' })

    try {
      const { results, bestConfidence } = await executeTier(tierName, params)
      updateTier(tierName, {
        status: 'complete', results, bestConfidence,
        timestamp: new Date().toISOString(),
        ...(tierName === 'expert' ? { expertName: extractExpertName(results) } : {}),
      })

      // If results found, generate instructions then pause
      if (tierName !== 'expert' && results.length > 0 && bestConfidence > 0) {
        updateTier(tierName, { isGeneratingInstructions: true })
        const instructions = await generateInstructions(params, results, tierName)
        updateTier(tierName, { generatedInstructions: instructions, isGeneratingInstructions: false })

        // Cache instructions in issue's ai_analysis
        if (instructions && params.issueId) {
          cacheInstructionsToIssue(params.issueId, tierName, instructions, results, bestConfidence)
        }

        setState((prev) => ({
          ...prev, isRunning: false, currentTier: null, pausedAtTier: tierName,
          bestOverallConfidence: Math.max(prev.bestOverallConfidence, bestConfidence),
        }))
      } else if (tierName === 'expert') {
        setState((prev) => ({
          ...prev, isRunning: false, currentTier: null, pausedAtTier: tierName,
          bestOverallConfidence: Math.max(prev.bestOverallConfidence, bestConfidence),
        }))
      } else {
        const next = getNextTier(tierName)
        if (next) await runTier(next, params)
        else setState((prev) => ({ ...prev, isRunning: false, currentTier: null }))
      }
    } catch (err) {
      updateTier(tierName, { status: 'error', error: String(err), timestamp: new Date().toISOString() })
      const next = getNextTier(tierName)
      if (next) await runTier(next, params)
      else setState((prev) => ({ ...prev, isRunning: false, currentTier: null }))
    }
  }, [updateTier])

  const runExpertAnalysis = useCallback(async (params: PipelineParams): Promise<PipelineParams> => {
    const expertFeature = getExpertFeatureForIssue(params.d365Module ?? null, null)
    const expertLabel = getExpertLabel(expertFeature)
    const expertDomain = getExpertiseDomain(expertFeature)

    setState((prev) => ({
      ...prev,
      expertAnalysis: { expertFeature, expertLabel, analysis: '', isRunning: true },
    }))

    try {
      // Step 1: Search KB for relevant knowledge before consulting expert
      let kbContext = ''
      try {
        const { data: kbData } = await supabase.functions.invoke('kb-search', {
          body: {
            query: params.description.slice(0, 1000) || 'D365 issue search',
            filters: {
              d365Version: params.d365Version,
              modules: params.d365Module ? [params.d365Module] : undefined,
              expertiseDomain: params.expertiseDomain || undefined,
            },
            options: { maxResults: 3, confidenceThreshold: 0.4 },
          },
        })
        const kbResults = kbData?.results ?? []
        if (kbResults.length > 0) {
          kbContext = kbResults.slice(0, 3).map((r: Record<string, string>, i: number) =>
            `KB ${i + 1}: ${r.title || ''}\n${r.description || r.content || ''}`
          ).join('\n\n')
        }
      } catch { /* KB search failed — continue without */ }

      // Step 2: Send issue + KB context to expert for concise first-pass analysis
      const envKey = `VITE_KAYCOO_${expertFeature.toUpperCase()}_AGENT_ID`
      const agentId = getAgentForFeature(expertFeature, import.meta.env[envKey] || '')
      if (!agentId) throw new Error(`No agent configured for ${expertFeature}`)

      const inputParts = [
        `Issue: ${params.description}`,
        `D365 Version: ${params.d365Version || 'Unknown'}`,
        `Module: ${params.d365Module || 'Unknown'}`,
      ]
      if (kbContext) {
        inputParts.push(`\nRelevant knowledge base entries:\n${kbContext}`)
      }
      inputParts.push('\nProvide a brief initial assessment (max 200 words): classify the issue, identify the likely root cause, and suggest the most relevant D365 menu path to investigate.')

      const result = await executeAgent(agentId, { input: inputParts.join('\n') })
      const analysis = result.response || result.result || result.output || ''

      setState((prev) => ({
        ...prev,
        expertAnalysis: { expertFeature, expertLabel, analysis, isRunning: false },
      }))

      // Save expert analysis to issue conversations
      if (params.issueId && analysis) {
        const { data: { user } } = await supabase.auth.getUser()
        const { data: userRow } = user
          ? await supabase.from('users').select('organization_id').eq('id', user.id).single()
          : { data: null }

        if (userRow?.organization_id) {
          await supabase.from('issue_conversations').insert({
            issue_id: params.issueId,
            organization_id: userRow.organization_id,
            role: 'assistant',
            content: analysis,
            is_internal: false,
            metadata: { agent_type: 'domain_expert', expert_feature: expertFeature, expert_label: expertLabel },
          } as never)
        }

        await supabase.from('issues').update({
          expertise_domain: expertDomain,
        } as never).eq('id', params.issueId)
      }

      return { ...params, expertiseDomain: expertDomain }
    } catch (err) {
      console.warn('[resolution-pipeline] Expert analysis failed:', err)
      setState((prev) => ({
        ...prev,
        expertAnalysis: prev.expertAnalysis
          ? { ...prev.expertAnalysis, isRunning: false }
          : null,
      }))
      return { ...params, expertiseDomain: expertDomain }
    }
  }, [])

  const startPipeline = useCallback(async (params: PipelineParams) => {
    paramsRef.current = params
    setState({
      isRunning: true, currentTier: null, pausedAtTier: null,
      tiers: [...INITIAL_TIERS], bestOverallConfidence: 0, acceptedTier: null, expertAnalysis: null,
    })

    // Step 0: Run domain expert analysis before KB search
    const enrichedParams = await runExpertAnalysis(params)
    paramsRef.current = enrichedParams

    setState((prev) => ({ ...prev, currentTier: 'kb' }))
    await runTier('kb', enrichedParams)
  }, [runTier])

  const acceptSolution = useCallback((tier: ResolutionTierName) => {
    const tierOrder: ResolutionTierName[] = ['kb', 'ms_docs', 'community', 'expert']
    const idx = tierOrder.indexOf(tier)
    for (let i = idx + 1; i < tierOrder.length; i++) updateTier(tierOrder[i], { status: 'skipped' })
    setState((prev) => ({ ...prev, pausedAtTier: null, acceptedTier: tier }))
  }, [updateTier])

  const rejectAndContinue = useCallback(async () => {
    const params = paramsRef.current
    if (!params || !state.pausedAtTier) return
    const next = getNextTier(state.pausedAtTier)
    if (next) await runTier(next, params)
    else setState((prev) => ({ ...prev, pausedAtTier: null }))
  }, [state.pausedAtTier, runTier])

  const skipToExpert = useCallback(async () => {
    const params = paramsRef.current
    if (!params) return
    const tierOrder: ResolutionTierName[] = ['kb', 'ms_docs', 'community']
    for (const t of tierOrder) {
      setState((prev) => ({
        ...prev,
        tiers: prev.tiers.map((x) => x.tier === t && x.status === 'pending' ? { ...x, status: 'skipped' } : x),
      }))
    }
    await runTier('expert', params)
  }, [runTier])

  /** Restore pipeline state from full ai_analysis object */
  const restoreFromCache = useCallback((aiAnalysis: Record<string, unknown>) => {
    const cachedInstructions = (aiAnalysis.cachedInstructions || {}) as Record<string, {
      instructions: GeneratedInstructions; results: Record<string, unknown>[]; confidence: number
    }>
    const resolvedTier = (aiAnalysis._acceptedTier as ResolutionTierName)
      || (aiAnalysis.resolvedByTier as ResolutionTierName)
      || null

    const restoredTiers: ResolutionTierResult[] = INITIAL_TIERS.map((t) => {
      const tierCache = cachedInstructions[t.tier]
      if (tierCache) {
        return {
          ...t, status: 'complete' as const, bestConfidence: tierCache.confidence || 0,
          results: tierCache.results || [], generatedInstructions: tierCache.instructions || null,
          timestamp: new Date().toISOString(),
        }
      }
      if (resolvedTier) {
        const tierOrder = ['kb', 'ms_docs', 'community', 'expert']
        const resolvedIdx = tierOrder.indexOf(resolvedTier)
        const thisIdx = tierOrder.indexOf(t.tier)
        if (thisIdx > resolvedIdx) return { ...t, status: 'skipped' as const }
      }
      return t
    })

    setState({
      isRunning: false, currentTier: null, pausedAtTier: null,
      tiers: restoredTiers, bestOverallConfidence: 0,
      acceptedTier: resolvedTier, expertAnalysis: null,
    })
  }, [])

  const reset = useCallback(() => {
    paramsRef.current = null
    setState({
      isRunning: false, currentTier: null, pausedAtTier: null,
      tiers: [...INITIAL_TIERS], bestOverallConfidence: 0, acceptedTier: null, expertAnalysis: null,
    })
  }, [])

  return { ...state, startPipeline, acceptSolution, rejectAndContinue, skipToExpert, restoreFromCache, reset }
}

// ─── Helpers ───

const TIER_ORDER: ResolutionTierName[] = ['kb', 'ms_docs', 'community', 'expert']

function getNextTier(current: ResolutionTierName): ResolutionTierName | null {
  const idx = TIER_ORDER.indexOf(current)
  return idx < TIER_ORDER.length - 1 ? TIER_ORDER[idx + 1] : null
}

function extractExpertName(results: Record<string, unknown>[]): string {
  if (!results.length) return 'Domain Expert'
  const first = results[0]
  return (first.expertName as string) ?? (first.assignedExpert as Record<string, string>)?.name ?? 'Domain Expert'
}

async function executeTier(
  tier: ResolutionTierName, params: PipelineParams,
): Promise<{ results: Record<string, unknown>[]; bestConfidence: number }> {
  switch (tier) {
    case 'kb': {
      const { data } = await supabase.functions.invoke('kb-search', {
        body: {
          query: params.description.slice(0, 1000) || 'D365 issue search',
          filters: {
            d365Version: params.d365Version,
            modules: params.d365Module ? [params.d365Module] : undefined,
            expertiseDomain: params.expertiseDomain || undefined,
          },
          options: { maxResults: 5, confidenceThreshold: 0.5 },
        },
      })
      const results = data?.results ?? []
      const best = Math.max(0, ...results.map((r: Record<string, number>) => r.similarity ?? r.compositeScore ?? 0))
      return { results, bestConfidence: best }
    }
    case 'ms_docs': {
      const { data } = await supabase.functions.invoke('external-doc-search', {
        body: { query: params.description, d365Version: params.d365Version, sources: ['d365_docs'], maxResults: 5 },
      })
      const results = data?.results ?? []
      const best = Math.max(0, ...results.map((r: Record<string, number>) => r.relevance ?? 0))
      return { results, bestConfidence: best }
    }
    case 'community': {
      const { data } = await supabase.functions.invoke('external-doc-search', {
        body: { query: params.description, d365Version: params.d365Version, sources: ['d365_community'], maxResults: 5 },
      })
      const results = data?.results ?? []
      const best = Math.max(0, ...results.map((r: Record<string, number>) => r.relevance ?? 0))
      return { results, bestConfidence: best }
    }
    case 'expert': {
      const { data } = await supabase.functions.invoke('expert-router', {
        body: { issueId: params.issueId, d365Module: params.d365Module, expertiseDomain: params.expertiseDomain },
      })
      return { results: data ? [data] : [], bestConfidence: 0 }
    }
  }
}

/**
 * Call a D365 expert agent to generate step-by-step instructions
 * from the search results. Uses the Technical Expert agent.
 */
async function generateInstructions(
  params: PipelineParams,
  results: Record<string, unknown>[],
  tier: ResolutionTierName,
): Promise<GeneratedInstructions | null> {
  try {
    const agentId = getAgentForFeature(
      'resolution_writer',
      import.meta.env.VITE_KAYCOO_RESOLUTION_WRITER_AGENT_ID || '93e2ce3b-3d64-4cec-9ec3-afeb5da42c32',
    )

    const sourceSummary = results.slice(0, 3).map((r, i) => {
      const title = (r.title as string) || `Result ${i + 1}`
      const snippet = (r.snippet as string) || (r.description as string) || ''
      const url = (r.url as string) || ''
      return `Source ${i + 1}: ${title}\n${snippet}\n${url}`
    }).join('\n\n')

    const prompt = `You are generating step-by-step resolution instructions for a D365 F&O issue.

ISSUE: ${params.description}
D365 VERSION: ${params.d365Version || 'Unknown'}
MODULE: ${params.d365Module || 'Unknown'}
SEARCH TIER: ${tier === 'kb' ? 'Internal Knowledge Base' : tier === 'ms_docs' ? 'Microsoft Learn' : 'D365 Community'}

SEARCH RESULTS:
${sourceSummary}

Based on these search results, generate clear step-by-step instructions that a D365 consultant can send to the end user. The instructions should be actionable and specific to D365 F&O.

Respond ONLY with this JSON (no other text):
{
  "summary": "Brief description of the solution (2-3 sentences)",
  "steps": [
    { "order": 1, "instruction": "Navigate to ...", "expectedResult": "You should see ..." },
    { "order": 2, "instruction": "Click on ...", "expectedResult": "The form opens ..." }
  ],
  "menuPath": "D365 menu path if applicable (e.g., Procurement > Purchase orders > Setup)",
  "warnings": ["Any important warnings or prerequisites"]
}`

    const result = await executeAgent(agentId, { input: prompt })
    const text = result.response || result.result || result.output || ''

    return parseInstructionsJSON(text)
  } catch (err) {
    console.error('Failed to generate instructions:', err)
    return null
  }
}

function parseInstructionsJSON(text: string): GeneratedInstructions | null {
  // Try fenced JSON
  const fenced = text.match(/```json\s*(\{[\s\S]*?\})\s*```/)
  if (fenced) {
    try { return JSON.parse(fenced[1]) } catch { /* continue */ }
  }
  // Try raw JSON
  const raw = text.match(/\{[\s\S]*"summary"[\s\S]*"steps"[\s\S]*\}/)
  if (raw) {
    try { return JSON.parse(raw[0]) } catch { /* continue */ }
  }
  // Fallback: use the whole text as summary
  if (text.trim()) {
    return { summary: text.trim(), steps: [] }
  }
  return null
}

/** Persist generated instructions to issue ai_analysis for caching */
async function cacheInstructionsToIssue(
  issueId: string, tier: ResolutionTierName,
  instructions: GeneratedInstructions, results: Record<string, unknown>[], confidence: number,
): Promise<void> {
  try {
    const { data } = await supabase.from('issues').select('ai_analysis').eq('id', issueId).single()
    const existing = (data?.ai_analysis as Record<string, unknown>) || {}
    const cached = (existing.cachedInstructions as Record<string, unknown>) || {}
    await supabase.from('issues').update({
      ai_analysis: {
        ...existing,
        cachedInstructions: { ...cached, [tier]: { instructions, results, confidence } },
      },
    } as never).eq('id', issueId)
  } catch (err) {
    console.warn('Failed to cache instructions:', err)
  }
}
